import {
  Text,
  Flex,
  QuantumColorPicker,
  QuantumRGB,
} from '@sendoutcards/quantum-design-ui'
import React from 'react'

import Color from 'color'
import { ColorPaletteType } from '@sendoutcards/editor'
import { useState } from 'src/hooks'
type ColorSettingType = {
  colorPalettes?: ColorPaletteType
  currentColor: QuantumRGB
  onSelectColor: (color: QuantumRGB) => void
}

export const ColorSetting = ({
  colorPalettes,
  currentColor,
  onSelectColor,
}: ColorSettingType) => {
  const [shouldShowMoreColors, setShouldShowMoreColors] = useState(false)

  const rgbToHex = (color: QuantumRGB) =>
    '#' +
    [color.r, color.g, color.b]
      .map(x => {
        const hex = x.toString(16)
        return hex.length === 1 ? '0' + hex : hex
      })
      .join('')

  const hexToRgb = (hex: string): QuantumRGB => {
    // Remove leading "#" if present
    const cleaned = hex.replace(/^#/, '')

    // Handle shorthand (e.g. #03F → #0033FF)
    const fullHex =
      cleaned.length === 3
        ? cleaned
            .split('')
            .map(c => c + c)
            .join('')
        : cleaned

    const num = parseInt(fullHex, 16)

    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255,
    }
  }
  //TODO: Could even add Recent Favorites to the UserSettings potentially
  const colors = colorPalettes?.palette?.map(palette => Color(palette).hex())
  const allColors = colors
    ? Array.from(
        new Set([rgbToHex(currentColor), ...colors].map(c => c.toLowerCase())),
      ).map(c => c)
    : [rgbToHex(currentColor)]
  const PALETTE_LENGTH = 10

  const handleColorClick = (hexColor: string) => {
    const rgbColor = hexToRgb(hexColor)
    onSelectColor(rgbColor)
  }

  return (
    <Flex flexDirection="column">
      <Flex
        flexDirection="row"
        width="100%"
        alignItems="center"
        justifyContent={shouldShowMoreColors ? 'center' : 'flex-start'}
      >
        <Text type="caption" style={labelStyle}>
          Color
        </Text>
        {shouldShowMoreColors && (
          <button
            style={{ ...gridButtonStyle, marginLeft: 'auto' }}
            onClick={() => setShouldShowMoreColors(false)}
          >
            <svg
              style={{ width: '16px', height: '16px' }}
              fill="#9CA3AF"
              viewBox="0 0 16 16"
            >
              <circle cx="4" cy="4" r="1.5" />
              <circle cx="12" cy="4" r="1.5" />
              <circle cx="4" cy="12" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
            </svg>
          </button>
        )}
      </Flex>
      {shouldShowMoreColors ? (
        <Flex width="312px">
          <QuantumColorPicker
            color={rgbToHex(currentColor)}
            onChange={(color: string) => onSelectColor(hexToRgb(color))}
            palette={{
              isLoading: false,
              colors: colors,
              title: 'Color Palette',
              count: PALETTE_LENGTH,
            }}
          />
        </Flex>
      ) : (
        <Flex style={colorRowStyle}>
          {allColors &&
            allColors.map(color => {
              const currentColorHex = rgbToHex(currentColor)
              return (
                <button
                  key={color}
                  onClick={() => handleColorClick(color)}
                  style={{
                    ...colorButtonStyle,
                    backgroundColor: color,
                    ...(currentColorHex.toLowerCase() === color.toLowerCase()
                      ? colorButtonActiveStyle
                      : {}),
                  }}
                  aria-label={`Select color ${color}`}
                />
              )
            })}
          <Flex
            flexDirection="row"
            style={{ marginLeft: 'auto' }}
            onClick={() => setShouldShowMoreColors(!shouldShowMoreColors)}
          >
            <button style={gridButtonStyle}>
              <svg
                style={{ width: '16px', height: '16px' }}
                fill="#9CA3AF"
                viewBox="0 0 16 16"
              >
                <circle cx="4" cy="4" r="1.5" />
                <circle cx="12" cy="4" r="1.5" />
                <circle cx="4" cy="12" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
              </svg>
            </button>
            <button style={newButtonStyle}>+ NEW</button>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6B7280',
  marginBottom: '8px',
  fontWeight: 'normal',
}

const colorRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}

const colorButtonStyle: React.CSSProperties = {
  width: '30px',
  height: '30px',
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  transition: 'transform 0.2s',
}

const colorButtonActiveStyle: React.CSSProperties = {
  boxShadow: '0 0 0 2px white, 0 0 0 4px #9CA3AF',
}

const newButtonStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#3B82F6',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  padding: '0 4px',
}

const gridButtonStyle: React.CSSProperties = {
  width: '30px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #D1D5DB',
  backgroundColor: 'white',
  borderRadius: '8px',
  cursor: 'pointer',
}
